import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Stack, TextField } from "@mui/material";
import {
  addOperationProcess,
  getOperationProcessById,
  updateOperationProcess,
} from "../../api/operationProcess";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import OperationProcessStepper, { newEmptyStep } from "./OperationProcessStepper";
import { DEFAULT_PASTEL_COLOR } from "./pastelColors";

export default function OperationProcessForm({ id, companyId, onSubmit }) {
  const { openSnack } = useSnack();
  const [loadingRecord, setLoadingRecord] = useState(Boolean(id));
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState([newEmptyStep()]);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      setLoadingRecord(true);
      try {
        const res = await getOperationProcessById(id);
        const data = res.data;
        setName(data.name ?? "");
        setDescription(data.description ?? "");
        setSteps(
          (data.steps ?? [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((s) => ({
              id: s.id,
              name: s.name,
              description: s.description ?? "",
              color: s.color || DEFAULT_PASTEL_COLOR,
            }))
        );
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoadingRecord(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name.trim()) {
      openSnack("El nombre del proceso es requerido", "warning");
      return;
    }
    if (steps.length === 0) {
      openSnack("El proceso debe tener al menos un paso", "warning");
      return;
    }
    if (steps.some((s) => !s.name.trim())) {
      openSnack("Todos los pasos deben tener un nombre", "warning");
      return;
    }

    const payload = {
      companyId,
      name,
      description,
      steps: steps.map((s, index) => ({
        id: s.id > 0 ? s.id : null,
        name: s.name,
        description: s.description ?? "",
        color: s.color || DEFAULT_PASTEL_COLOR,
        position: index,
      })),
    };

    setSubmitting(true);
    try {
      if (id) {
        await updateOperationProcess(id, payload);
        openSnack("Proceso de operación actualizado con éxito", "success");
      } else {
        await addOperationProcess(payload);
        openSnack("Proceso de operación creado con éxito", "success");
      }
      onSubmit?.();
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingRecord) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <TextField
          label="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Descripción"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <OperationProcessStepper mode="edit" steps={steps} onChange={setSteps} />
        <Button type="submit" variant="contained" color="primary" disabled={submitting} fullWidth>
          {id ? "Actualizar" : "Crear"}
        </Button>
      </Stack>
    </Box>
  );
}
